import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { EvaluationFeedbackCriteriaStateModel } from 'models/feedback/feedback.model';
import {
  activeEvaluationFeedbackCriteriaAction,
  createEvaluationFeedbackCriteriaAction,
  deleteEvaluationFeedbackCriteriaAction,
  getEvaluationFeedbackCriteriaAction,
  inactiveEvaluationFeedbackCriteriaAction,
  listEvaluationFeedbackCriteriaAction,
  updateEvaluationFeedbackCriteriaAction,
} from './feedback.action';

const initialState: EvaluationFeedbackCriteriaStateModel = {
  evaluationFeedbackCriteriaListData: null,
  evaluationFeedbackCriteriaDetail: null,
  triggerSaveEvaluationFeedbackCriteria: null,

  loadingList: false,
  loadingDetail: false,
};

export const evaluationFeedbackCriteriaSlice = createSlice({
  name: 'evaluationFeedbackCriteria',
  initialState,
  reducers: {
    setEvaluationFeedbackCriteriaStateAction: (
      state,
      action: PayloadAction<Partial<EvaluationFeedbackCriteriaStateModel>>,
    ) =>
      ({
        ...state,
        ...action?.payload,
      } as EvaluationFeedbackCriteriaStateModel),
    resetEvaluationFeedbackCriteriaStateAction: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEvaluationFeedbackCriteriaAction.pending, (state) => ({
        ...state,
        evaluationFeedbackCriteriaDetail: null,
        loadingDetail: true,
      }))
      .addCase(
        getEvaluationFeedbackCriteriaAction.fulfilled,
        (state, { payload }) => ({
          ...state,
          evaluationFeedbackCriteriaDetail: payload?.data || null,
          loadingDetail: false,
        }),
      )
      .addCase(getEvaluationFeedbackCriteriaAction.rejected, (state) => ({
        ...state,
        evaluationFeedbackCriteriaDetail: null,
        loadingDetail: false,
      }))
      .addCase(listEvaluationFeedbackCriteriaAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(
        listEvaluationFeedbackCriteriaAction.fulfilled,
        (state, { payload }) => ({
          ...state,
          loadingList: false,
          evaluationFeedbackCriteriaListData: {
            items: payload?.data?.items || [],
            totalItems: payload?.data?.totalItems || 0,
            totalPages: payload?.data?.totalPages || 0,
          },
        }),
      )
      .addCase(listEvaluationFeedbackCriteriaAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(createEvaluationFeedbackCriteriaAction.fulfilled, (state) => ({
        ...state,
        triggerSaveEvaluationFeedbackCriteria: { id: uuidv4() },
      }))
      .addCase(updateEvaluationFeedbackCriteriaAction.fulfilled, (state) => ({
        ...state,
        triggerSaveEvaluationFeedbackCriteria: { id: uuidv4() },
      }))

      .addCase(deleteEvaluationFeedbackCriteriaAction.pending, (state) => ({
        ...state,
        loadingList: true,
      }))
      .addCase(deleteEvaluationFeedbackCriteriaAction.fulfilled, (state) => ({
        ...state,
        triggerSaveEvaluationFeedbackCriteria: { id: uuidv4() },
      }))
      .addCase(deleteEvaluationFeedbackCriteriaAction.rejected, (state) => ({
        ...state,
        loadingList: false,
      }))
      .addCase(activeEvaluationFeedbackCriteriaAction.fulfilled, (state) => ({
        ...state,
        triggerSaveEvaluationFeedbackCriteria: { id: uuidv4() },
        loadingList: false,
      }))
      .addCase(inactiveEvaluationFeedbackCriteriaAction.fulfilled, (state) => ({
        ...state,
        triggerSaveEvaluationFeedbackCriteria: { id: uuidv4() },
        loadingList: false,
      }));
  },
});

export const {
  setEvaluationFeedbackCriteriaStateAction,
  resetEvaluationFeedbackCriteriaStateAction,
} = evaluationFeedbackCriteriaSlice.actions;

export default evaluationFeedbackCriteriaSlice.reducer;
